/**
 * Threads Service
 * Manages conversation threads and messages
 */

import { TeamScopedService } from './base.service';
import type { Thread, Message } from '@zenmako/contracts/threads';

export interface CreateThreadRequest {
  agentId: string;
  title?: string;
  metadata?: Record<string, any>;
}

export interface CreateThreadResponse {
  thread: Thread;
}

export interface ListThreadsResponse {
  threads: Array<{
    threadId: string;
    resourceId: string;
    agentId?: string;
    title?: string;
    lastMessage?: string;
    lastActivity: string;
    messageCount: number;
    createdAt: string;
  }>;
  count: number;
  hasMore: boolean;
}

export interface GetThreadResponse {
  thread: Thread;
}

export interface GetMessagesResponse {
  messages: Message[];
  total?: number;
}

export interface UpdateThreadRequest {
  title?: string;
  metadata?: Record<string, any>;
}

class ThreadsService extends TeamScopedService {
  /**
   * Create a new thread for an agent
   */
  async createThread(
    teamId: string,
    request: CreateThreadRequest
  ): Promise<Thread> {
    const body = {
      resourceId: `t#${teamId}`,
      metadata: {
        agentId: request.agentId,
        title: request.title,
        ...request.metadata,
      },
    };

    const response = await this.postTeam<CreateThreadResponse>(teamId, '/threads', body);
    return response.thread;
  }

  /**
   * Get a specific thread by ID
   */
  async getThread(teamId: string, threadId: string): Promise<Thread> {
    const response = await this.getTeam<GetThreadResponse>(teamId, `/threads/${threadId}`);
    return response.thread;
  }

  /**
   * List all threads for a team
   */
  async getThreads(
    teamId: string,
    options?: {
      agentId?: string;
      page?: number;
      perPage?: number;
      orderBy?: string;
      sortDirection?: 'ASC' | 'DESC';
    }
  ): Promise<ListThreadsResponse> {
    const params = new URLSearchParams();

    if (options?.agentId) {
      params.set('agentId', options.agentId);
    }
    if (options?.page !== undefined) {
      params.set('page', options.page.toString());
    }
    if (options?.perPage) {
      params.set('perPage', options.perPage.toString());
    }
    if (options?.orderBy) {
      params.set('orderBy', options.orderBy);
    }
    if (options?.sortDirection) {
      params.set('sortDirection', options.sortDirection);
    }

    return this.getTeam<ListThreadsResponse>(teamId, `/threads?${params.toString()}`);
  }

  /**
   * Update a thread
   */
  async updateThread(
    teamId: string,
    threadId: string,
    updates: UpdateThreadRequest
  ): Promise<Thread> {
    const response = await this.patchTeam<GetThreadResponse>(
      teamId,
      `/threads/${threadId}`,
      updates
    );
    return response.thread;
  }

  /**
   * Delete a thread
   */
  async deleteThread(teamId: string, threadId: string): Promise<void> {
    await this.deleteTeam(teamId, `/threads/${threadId}`);
  }

  /**
   * Get messages for a thread
   */
  async getMessages(
    teamId: string,
    threadId: string,
    options?: {
      limit?: number;
      before?: string;
      after?: string;
    }
  ): Promise<Message[]> {
    const params = new URLSearchParams();

    if (options?.limit) {
      params.set('limit', options.limit.toString());
    }
    if (options?.before) {
      params.set('before', options.before);
    }
    if (options?.after) {
      params.set('after', options.after);
    }

    const queryString = params.toString();
    const path = `/threads/${threadId}/messages${queryString ? `?${queryString}` : ''}`;

    const response = await this.getTeam<GetMessagesResponse>(teamId, path);
    return response.messages || [];
  }
}

export const threadsService = new ThreadsService();
